import React, { useEffect, useState } from 'react';
import * as notificationsApi from '../../utils/notificationsApi';
import type { NotificationItem } from '../../utils/notificationsApi';
import { useNavigate } from 'react-router-dom';

const formatTime = (ts: number) => {
  try {
    const d = new Date(ts);
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};

const NotificationsPage: React.FC<{ onUnreadCountChange?: (next: number) => void; unreadCount?: number }> = ({
  onUnreadCountChange,
  unreadCount,
}) => {
  const navigate = useNavigate();
  const [scope, setScope] = useState<'all' | 'unread'>('all');
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [before, setBefore] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState(true);

  const load = async (reset = false) => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await notificationsApi.listNotifications({
        scope,
        limit: 50,
        before: reset ? undefined : before,
      });
      const next = resp.items || [];
      setItems((prev) => (reset ? next : [...prev, ...next]));
      if (next.length > 0) {
        setBefore(next[next.length - 1].created_at_ms);
      }
      setHasMore(next.length === 50);
    } catch (e: any) {
      setError(e?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setItems([]);
    setBefore(undefined);
    setHasMore(true);
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const markReadAndNavigate = async (n: NotificationItem) => {
    try {
      if (!n.is_read) {
        await notificationsApi.markNotificationRead(n.notification_id);
        if (typeof unreadCount === 'number' && onUnreadCountChange) {
          onUnreadCountChange(Math.max(0, unreadCount - 1));
        }
        setItems((prev) => prev.map((x) => (x.notification_id === n.notification_id ? { ...x, is_read: true } : x)));
      }
    } catch {
      // ignore
    } finally {
      if (n.link_url) navigate(n.link_url);
    }
  };

  const readAll = async () => {
    try {
      await notificationsApi.readAllNotifications(500);
      if (typeof unreadCount === 'number' && onUnreadCountChange) onUnreadCountChange(0);
      setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
    } catch (e: any) {
      setError(e?.message || 'Failed to mark all read');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-6">
      <div className="rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-secondary overflow-hidden shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-border flex items-center justify-between gap-3">
          <div className="inline-flex rounded-lg bg-gray-100 dark:bg-dark-bg-tertiary p-1">
            <button
              type="button"
              onClick={() => setScope('all')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                scope === 'all'
                  ? 'bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-dark-text-primary shadow-sm'
                  : 'text-gray-600 dark:text-dark-text-muted hover:text-gray-800 dark:hover:text-dark-text-primary'
              }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setScope('unread')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                scope === 'unread'
                  ? 'bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-dark-text-primary shadow-sm'
                  : 'text-gray-600 dark:text-dark-text-muted hover:text-gray-800 dark:hover:text-dark-text-primary'
              }`}
            >
              Unread
            </button>
          </div>

          <button
            type="button"
            onClick={readAll}
            className="h-9 inline-flex items-center px-3 rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-secondary text-sm font-medium text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover"
          >
            Mark all read
          </button>
        </div>

        {error && <div className="px-4 py-3 text-sm text-red-700 dark:text-red-300">{error}</div>}
        {items.length === 0 && !loading ? (
          <div className="px-4 py-10 text-sm text-gray-600 dark:text-dark-text-muted">No notifications.</div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-dark-border">
            {items.map((n) => (
              <button
                key={n.notification_id}
                type="button"
                onClick={() => markReadAndNavigate(n)}
                className="w-full text-left px-4 py-4 hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
              >
                <div className="flex items-start gap-3">
                  {n.image_url ? (
                    <img
                      src={n.image_url}
                      alt=""
                      className="w-12 h-12 rounded-lg object-cover bg-gray-100 dark:bg-dark-bg-tertiary flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-dark-bg-tertiary flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary truncate">
                        {n.title || 'Notification'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-dark-text-muted whitespace-nowrap">
                        {formatTime(n.created_at_ms)}
                      </div>
                    </div>
                    <div className="text-sm text-gray-700 dark:text-dark-text-secondary">{n.body}</div>
                    {!n.is_read && (
                      <div className="mt-2">
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600">
                          <span className="w-2 h-2 rounded-full bg-blue-600" />
                          Unread
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {hasMore && (
          <div className="px-4 py-4 border-t border-gray-200 dark:border-dark-border flex items-center justify-center">
            <button
              type="button"
              onClick={() => load(false)}
              disabled={loading}
              className="h-10 inline-flex items-center px-4 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;


